import * as XLSX from 'xlsx'


export const BATCH_IMPORT_COLUMNS = [
    { key: 'empNo', header: 'Service No' },
    { key: 'amount', header: 'Amount' },
    // { key: 'percentage', header: 'Percentage (Optional override)' },
    // { key: 'overrideReason', header: 'Override Reason' },
    // { key: 'remarks', header: 'Remarks' },
];

export const downloadBatchEmployeeTemplate = (batchData) => {
  const wb = XLSX.utils.book_new();

  // Sample data row
  const sampleData = [
    {
      'Service No': '2400123',
      'Amount': batchData?.amount ?? 5000,
      // 'Percentage (Optional override)': batchData?.calculationMethod === 'Percentage' ? 10 : '',
      // 'Override Reason': 'Standard allocation',
      // 'Remarks': 'Added via template',
    },
  ];

  const ws = XLSX.utils.json_to_sheet(sampleData, {
    header: BATCH_IMPORT_COLUMNS.map((c) => c.header),
  });

  ws['!cols'] = [{ wch: 20 }, { wch: 16 }];

  XLSX.utils.book_append_sheet(wb, ws, 'BatchEmployees');
  XLSX.writeFile(wb, `Payroll_Batch_${batchData?.batchNo || 'Import'}_Template.xlsx`);
};


const norm = (s) => String(s).toLowerCase().replace(/\*/g, '').replace(/\s+/g, ' ').trim();

const pick = (row, test) => {
  const key = Object.keys(row).find((k) => test(norm(k)));
  return key !== undefined ? String(row[key] ?? '').trim() : '';
};

export const readBatchWorkbook = async (file) => {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const sheetName = wb.SheetNames.find((s) => /batchemp/i.test(s)) || wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];

  if (!ws) return [];

  const rawRows = XLSX.utils.sheet_to_json(ws, { defval: '' });

  // return rawRows.map((row, idx) => {
  //   const getValue = (patterns) => {
  //     const foundKey = Object.keys(row).find((k) =>
  //       patterns.some((p) => k.toLowerCase().trim().includes(p.toLowerCase()))
  //     );
  //     return foundKey !== undefined ? String(row[foundKey]).trim() : '';
  //   };

  //   return {
  //       _row: idx + 2,
  //       empNo: String(row['Staff ID / Service No *'] ?? row['Staff ID / Service No'] ?? '').trim(),
  //       amount: String(row['Amount (Optional override)'] ?? row['Amount'] ?? '').trim(),
  //       percentage: String(row['Percentage (Optional override)'] ?? row['Percentage'] ?? '').trim(),
  //       overrideReason: String(row['Override Reason'] ?? row['Reason'] ?? '').trim(),
  //       remarks: String(row['Remarks'] ?? row['Remark'] ?? '').trim(),
  //   };
  // });

  return rawRows.map((row, idx) => {
    return {
      _row: idx + 2,
      empNo: String(
        row['Service No'] ?? 
        row['ServiceNo'] ?? 
        row['Staff ID / Service No *'] ?? 
        row['Staff ID / Service No'] ?? 
        ''
      ).trim(),
      amount: String(
        row['Amount'] ?? 
        row['Amount (Optional override)'] ?? 
        ''
      ).trim(),
    };
  })
};

export const validateBatchRow = (row, existingEmployeesMap, existingBatchEmpNos, duplicateInFileSet = new Set()) => {
  const errors = [];
  const empNo = String(row.empNo || '').trim();
  const lowerEmpNo = empNo.toLowerCase();

  if (!empNo) {
    errors.push('Service No is required');
  } else {
    // Check duplicates within Excel
    if (duplicateInFileSet.has(lowerEmpNo)) {
      errors.push(`Duplicate Service No "${empNo}" found multiple times in this Excel file`);
    }

    // Check existence in master employees
    const emp = existingEmployeesMap[lowerEmpNo];
    if (!emp) {
      errors.push(`Staff ID "${empNo}" does not exist in the employee system`);
    } else {
      // Check if employee is inactive
      const isActive = emp.HED_IS_ACTIVE === true || emp.HED_IS_ACTIVE === 'True' || emp.HED_IS_ACTIVE === 1 || emp.HED_IS_ACTIVE === '1';
      if (!isActive) {
        errors.push(`Employee "${empNo}" is inactive`);
      }
    }

    // Check existence in current batch
    if (existingBatchEmpNos.has(empNo.toLowerCase())) {
      errors.push(`Employee "${empNo}" is already added/active in this batch`);
    }
  }

  // Validate the amount is numeric
  const parsedAmount = row.amount ? Number(String(row.amount).replace(/,/g, '')) : undefined;
  if (row.amount && Number.isNaN(parsedAmount)) {
    errors.push(`Invalid amount value "${row.amount}"`)
  }

  const rawAmount = String(row.amount ?? '').replace(/,/g, '').trim();
  let amount;
  if (rawAmount !== '') {
    amount = Number(rawAmount);
    if (Number.isNaN(amount)) errors.push(`Amount "${row.amount}" is not a number`);
    else if (amount < 0) errors.push('Amount cannot be negative');
  }

  return {
    values: {
      ...row,
      empNo,
      amount: parsedAmount,
    },
    errors,
  };
};

/**
 * Downloads a template containing all recurring active fixed batches as columns:
 * Service No | BATCH001 | BATCH002 | ...
 */
export const downloadMultiBatchTemplate = (batches = []) => {
  const wb = XLSX.utils.book_new();

  // Only take recurring batches (where periodId is null, undefined, or empty string)
  const recurringBatches = batches.filter((b) => {
    return b.periodId === null || b.periodId === undefined || String(b.periodId).trim() === '';
  });

  const batchHeaders = recurringBatches
    .map((b) => b.batchNo || b.code)
    .filter(Boolean);

  const headers = ['Service No', ...batchHeaders];

  // Example row
  const sampleRow = { 'Service No': '000111' };
  if (batchHeaders.length > 0) sampleRow[batchHeaders[0]] = 15000;
  if (batchHeaders.length > 1) sampleRow[batchHeaders[1]] = '';

  const ws = XLSX.utils.json_to_sheet([sampleRow], { header: headers });

  ws['!cols'] = [{ wch: 18 }, ...batchHeaders.map(() => ({ wch: 16 }))];

  XLSX.utils.book_append_sheet(wb, ws, 'BatchImport');
  XLSX.writeFile(wb, 'Recurring_Batch_Employees_Template.xlsx');
};

/**
 * Reads workbook and returns rows with dynamic batch assignments
 */
export const readMultiBatchWorkbook = async (file) => {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];

  if (!ws) return [];

  const rawRows = XLSX.utils.sheet_to_json(ws, { defval: '' });

  return rawRows.map((row, idx) => {
    // Locate Service No column key
    const serviceNoKey = Object.keys(row).find((k) =>
      ['service no', 'serviceno', 'service_no', 'staff id', 'employee no'].includes(
        k.toLowerCase().replace(/[\*_-]/g, ' ').trim()
      )
    ) || 'Service No';

    const empNo = String(row[serviceNoKey] ?? '').trim();

    // All remaining keys represent batch numbers
    const batchEntries = {};
    Object.keys(row).forEach((k) => {
      if (k !== serviceNoKey) {
        batchEntries[k.trim()] = String(row[k] ?? '').trim();
      }
    });

    return {
      _row: idx + 2,
      empNo,
      batchEntries,
    };
  });
};

/**
 * Validates each row against employee and batch lookups
 */
export const validateMultiBatchRow = (
  row,
  employeeMap,
  batchMapByNo,
  duplicateInFileSet = new Set(),
  existingBatchEmpMap = {}
) => {
  const errors = [];
  const empNo = String(row.empNo || '').trim();
  const lowerEmpNo = empNo.toLowerCase();

  let emp = null;

  if (!empNo) {
    errors.push('Service No is required');
  } else {
    if (duplicateInFileSet.has(lowerEmpNo)) {
      errors.push(`Duplicate Service No "${empNo}" found multiple times in file`);
    }

    emp = employeeMap[lowerEmpNo];
    if (!emp) {
      errors.push(`Employee "${empNo}" not found`);
    } else {
      const isActive =
        emp.HED_IS_ACTIVE === true ||
        emp.HED_IS_ACTIVE === 'True' ||
        emp.HED_IS_ACTIVE === 1 ||
        emp.HED_IS_ACTIVE === '1';
      if (!isActive) {
        errors.push(`Employee "${empNo}" is inactive`);
      }
    }
  }

  // Parse batch entries
  const assignments = [];
  const alreadyInBatches = [];
  let enteredBatchesCount = 0;

  Object.entries(row.batchEntries).forEach(([batchNo, rawValue]) => {
    if (rawValue === '') return; // Not included in this batch

    enteredBatchesCount++;
    const targetBatch = batchMapByNo[batchNo.toLowerCase()];

    if (!targetBatch) {
      errors.push(`Batch code "${batchNo}" does not exist`);
      return;
    }

    const batchKey = (targetBatch.batchNo || batchNo).trim().toLowerCase();
    if (existingBatchEmpMap[batchKey]?.has(lowerEmpNo)) {
      alreadyInBatches.push(targetBatch.batchNo || batchNo);
    }

    const cleanVal = String(rawValue).replace(/,/g, '');
    const numVal = Number(cleanVal);

    if (Number.isNaN(numVal)) {
      errors.push(`Batch "${batchNo}": "${rawValue}" is not a valid amount`);
    } else if (numVal < 0) {
      errors.push(`Batch "${batchNo}": Amount cannot be negative`);
    } else {
      assignments.push({
        batchId: targetBatch.id,
        batchNo: targetBatch.batchNo,
        amount: numVal,
      });
    }
  });

  if (alreadyInBatches.length > 0) {
    errors.push(`Employee ${empNo} is already in batch ${alreadyInBatches.join(', ')}`);
  }

  if (empNo && enteredBatchesCount === 0) {
    errors.push('No batch amounts specified for this employee');
  }

  return {
    _row: row._row,
    empNo,
    employeeName: emp ? (emp.HED_REPORT_NAME || `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`).trim() : '—',
    employeeId: emp?.HED_EMPLOYEE_ID || emp?.employeeId,
    batchEntries: row.batchEntries,
    assignments,
    errors,
  };
};